// i18next runtime. Resources are bundled and init is fully synchronous (no async
// backend, no Suspense) so translations resolve during the Node static-web
// render — otherwise the exported HTML would ship untranslated.
//
// Routing owns the active locale (the `[lang]` URL segment), not client-side
// device detection. Each locale gets its own cloned, permanently-fixed i18next
// instance via getInstanceForLocale — never call changeLanguage() on the shared
// `i18n` default export. Static export can render /en, /fr and /ru pages in the
// same Node process, so a single mutable "current language" would race.
//
// The texts are the app's: it passes its bundled resources to initI18n() once
// (the app's src/lib/i18n/index.ts) and re-exports what comes back.

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import { DEFAULT_LOCALE, SUPPORTED_LOCALES, type Locale } from './locales';

/** Each locale's bundled strings, under i18next's default `translation` namespace. */
export type I18nResources = Record<Locale, { translation: object }>;

/** Initialises the shared i18next instance with the app's resources (once). */
export function initI18n(resources: I18nResources) {
  if (i18n.isInitialized) return i18n;
  // eslint-disable-next-line import/no-named-as-default-member -- i18next's documented chainable API
  i18n.use(initReactI18next).init({
    resources,
    lng: DEFAULT_LOCALE,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: SUPPORTED_LOCALES as unknown as string[],
    defaultNS: 'translation',
    interpolation: {
      // React Native / RN-web already escape rendered text; i18next must not.
      escapeValue: false,
    },
    returnNull: false,
    // Resources are bundled (no backend), so loading is already synchronous;
    // this keeps init's internal scheduling synchronous too, so callers below
    // never need to await it.
    initAsync: false,
    react: {
      useSuspense: false,
    },
  });
  return i18n;
}

// One instance per locale, created once and never mutated afterwards. Each has
// a fixed `.language`, so there's no shared mutable state to race on when the
// static exporter renders multiple locales' pages in one process.
const localeInstances = new Map<Locale, typeof i18n>();

export function getInstanceForLocale(locale: Locale): typeof i18n {
  const existing = localeInstances.get(locale);
  if (existing) return existing;
  const clone = i18n.cloneInstance({ lng: locale, initAsync: false });
  localeInstances.set(locale, clone);
  return clone;
}

